// ============================================================================
// Construction Types and Definitions
// ============================================================================
// Beautiful, scalable construction system with server authority
// ============================================================================
import { TICK_INTERVAL_MS } from '../constants.js';
import { OBJECT_PROPERTIES, ObjectCategory, ObjectType } from '../objects/types.js';
// Construction categories for UI organization
export const ConstructionCategory = {
    STRUCTURE: 'structure',
    FURNITURE: 'furniture',
    WORKSTATION: 'workstation',
    FLOOR: 'floor',
};
// Get all buildable objects from OBJECT_PROPERTIES
const getBuildableObjects = () => {
    const buildable = [];
    for (const [typeStr, props] of Object.entries(OBJECT_PROPERTIES)) {
        if (props.buildable) {
            buildable.push(Number(typeStr));
        }
    }
    return buildable;
};
// Map object category to construction category
const mapToConstructionCategory = ({ objectCategory, }) => {
    switch (objectCategory) {
        case ObjectCategory.STRUCTURE:
            return ConstructionCategory.STRUCTURE;
        case ObjectCategory.FURNITURE:
            return ConstructionCategory.FURNITURE;
        case ObjectCategory.WORKSTATION:
            return ConstructionCategory.WORKSTATION;
        default:
            return ConstructionCategory.STRUCTURE;
    }
};
// Hotkey mapping for common structures
// Note: 'B' is reserved for Build panel toggle
// Note: 'S' removed - conflicts with camera movement (WASD)
const HOTKEY_MAP = {
    [ObjectType.WALL_WOOD]: '1',
    [ObjectType.WALL_STONE]: '2',
    [ObjectType.WALL_METAL]: '3',
    [ObjectType.DOOR_WOOD]: '4',
    [ObjectType.CAMPFIRE]: 'C',
    [ObjectType.CRAFTING_TABLE]: 'T',
    [ObjectType.BED]: 'E', // 'E' for rEst/bEd
};
// Description mapping
const DESCRIPTION_MAP = {
    [ObjectType.WALL_WOOD]: 'A basic wooden wall. Provides shelter but burns easily.',
    [ObjectType.WALL_STONE]: 'A sturdy stone wall. Fire-resistant and durable.',
    [ObjectType.WALL_METAL]: 'A reinforced metal wall. Maximum protection.',
    [ObjectType.DOOR_WOOD]: 'A wooden door. Allows passage when opened.',
    [ObjectType.DOOR_METAL]: 'A metal door. Secure and durable.',
    [ObjectType.FLOOR_WOOD]: 'Wooden flooring. Improves movement speed.',
    [ObjectType.FLOOR_STONE]: 'Stone flooring. Durable and fire-resistant.',
    [ObjectType.BED]: 'A comfortable bed for resting.',
    [ObjectType.TABLE]: 'A table for eating and working.',
    [ObjectType.CHAIR]: 'A chair for sitting.',
    [ObjectType.STORAGE_CRATE]: 'A crate for storing items.',
    [ObjectType.STORAGE_SHELF]: 'A shelf for organized storage.',
    [ObjectType.CRAFTING_TABLE]: 'A workbench for crafting items.',
    [ObjectType.ANVIL]: 'An anvil for smithing weapons and tools.',
    [ObjectType.FURNACE]: 'A furnace for smelting ores into bars.',
    [ObjectType.CAMPFIRE]: 'A campfire for warmth and cooking.',
    [ObjectType.COOKING_STOVE]: 'A stove for advanced cooking.',
};
// Generate construction definitions from object properties
const generateConstructionDefinitions = () => {
    const definitions = new Map();
    for (const objectType of getBuildableObjects()) {
        const props = OBJECT_PROPERTIES[objectType];
        if (!props) {
            continue;
        }
        const definition = {
            objectType,
            name: props.name,
            description: DESCRIPTION_MAP[objectType] ?? `Build a ${props.name.toLowerCase()}.`,
            category: mapToConstructionCategory({ objectCategory: props.category }),
            width: props.width,
            height: props.height,
            buildTime: props.buildTime ?? 100,
            buildCost: props.buildCost ?? [],
            skillRequired: 'construction',
            minSkillLevel: 0,
            hotkey: HOTKEY_MAP[objectType] ?? null,
            unlocked: true,
        };
        definitions.set(objectType, definition);
    }
    return definitions;
};
// Singleton construction definitions
export const CONSTRUCTION_DEFINITIONS = generateConstructionDefinitions();
// Get construction definition by object type
export const getConstructionDefinition = ({ objectType, }) => {
    return CONSTRUCTION_DEFINITIONS.get(objectType) ?? null;
};
// Get all construction definitions
export const getAllConstructionDefinitions = () => {
    return Array.from(CONSTRUCTION_DEFINITIONS.values());
};
// Get construction definitions by category
export const getConstructionsByCategory = ({ category, }) => {
    return getAllConstructionDefinitions().filter((def) => def.category === category);
};
// Get construction definition by hotkey
export const getConstructionByHotkey = ({ hotkey, }) => {
    const upperHotkey = hotkey.toUpperCase();
    for (const def of CONSTRUCTION_DEFINITIONS.values()) {
        if (def.hotkey?.toUpperCase() === upperHotkey) {
            return def;
        }
    }
    return null;
};
// Construction site state
export const ConstructionState = {
    PENDING: 0, // Awaiting resources / workers
    IN_PROGRESS: 1, // Being actively worked on
    PAUSED: 2, // Construction paused
    COMPLETE: 3, // Finished
    CANCELLED: 4, // Cancelled by player
};
// Note: ConstructionSiteSnapshot is defined in protocol/messages.ts for network serialization
// Calculate construction duration based on skill level
export const getConstructionDuration = ({ baseBuildTime, skillLevel, workerCount = 1, }) => {
    // Base duration in ms (baseBuildTime is in ticks)
    const baseMs = baseBuildTime * TICK_INTERVAL_MS;
    // Skill bonus: 5% faster per level (up to 50% at level 10)
    const skillMultiplier = 1 - Math.min(0.5, skillLevel * 0.05);
    // Multiple workers: diminishing returns
    // 1 worker = 100%, 2 workers = 160%, 3 workers = 200%, etc.
    const workerMultiplier = 1 / (1 + Math.log2(Math.max(1, workerCount)) * 0.6);
    return Math.max(500, baseMs * skillMultiplier * workerMultiplier);
};
// Get average skill level for multiple workers
export const getAverageConstructionSkill = ({ workerSkillLevels, }) => {
    if (workerSkillLevels.length === 0) {
        return 0;
    }
    const total = workerSkillLevels.reduce((sum, level) => sum + level, 0);
    return total / workerSkillLevels.length;
};
//# sourceMappingURL=types.js.map